import * as React from "react"

import { Button, Skeleton } from "@ibada/ui"

import type { MoneyString } from "@ibada/shared-types/finance"

import { useTranslations } from "@/i18n"
import { useModulePermission } from "@/features/auth/hooks/use-module-permission"
import { WorkspaceAccessModule } from "@/features/auth/model/auth-types"
import { useActiveBudgetOverviewQuery } from "@/features/budgeting/services/budgets-query"
import { BudgetHealth, BudgetScope } from "@/features/budgeting/model/budget-enums"
import { useWidgetMenu } from "@ibada/dashboard-engine"

import { ProgressBar, type ProgressTone } from "@/features/finance/overview/components/progress-bar"
import { WidgetBoundary } from "@/features/finance/overview/components/widget-boundary"
import {
  useFinanceOverviewPeriod,
  useFinanceOverviewScope,
} from "@/features/finance/overview/context/finance-overview-context"
import { formatMoneySafe, formatPercent, moneyUtilizationPercent } from "@/features/finance/overview/lib/overview-format"
import { OverviewRoutes, useOverviewNav } from "@/features/finance/overview/lib/overview-nav"
import {
  useWidgetCreateActions,
  type WidgetCreateKey,
} from "@/features/finance/overview/lib/use-widget-create-actions"

import {
  ConfigStepperRow,
  WidgetConfigDrawer,
  useWidgetConfig,
} from "@/features/finance/overview/components/widget-config-drawer"

const WIDGET_CREATE_KEYS: readonly WidgetCreateKey[] = ["budget", "expense"]

const DEFAULT_ROWS = 4

interface BudgetHealthConfig {
  maxRows: number
}

/** Server-computed budget health -> progress-bar tone. Never recomputed on the client. */
function toneForHealth(health: BudgetHealth): ProgressTone {
  switch (health) {
    case BudgetHealth.Healthy:
      return "success"
    case BudgetHealth.Warning:
      return "warning"
    case BudgetHealth.Critical:
      return "destructive"
    default:
      return "muted"
  }
}

interface BudgetRow {
  id: string
  title: string
  /** Canonical decimal money strings (IBA-116) — rendered exactly, never converted to a float first. */
  planned: MoneyString
  actual: MoneyString
  /** A PERCENTAGE, not money: unitless, so a number is exactly what it is. */
  utilization: number
  /** The currency this row's amounts are denominated in — the budget states it; we never assume it. */
  currency: string
  tone: ProgressTone
}

/**
 * Budget Health — whether active budgets stay within plan for the selected period. Period-sensitive:
 * actuals depend on the dashboard period. Health status comes from the server (BudgetHealth), so the
 * widget only maps it to a tone and renders utilization progress bars — it never re-derives health.
 */
export function BudgetHealthWidget() {
  const t = useTranslations("finance.overview")
  const scope = useFinanceOverviewScope()
  const period = useFinanceOverviewPeriod()
  const go = useOverviewNav()
  const budgeting = useModulePermission(WorkspaceAccessModule.BUDGETING)

  const query = useActiveBudgetOverviewQuery(
    {
      workspaceId: scope.workspaceId ?? undefined,
      context: scope.context === "parish" ? BudgetScope.Parish : BudgetScope.Branch,
      branchId: scope.branchId ?? undefined,
      dateFrom: period.from,
      dateTo: period.to,
    },
    { enabled: Boolean(scope.workspaceId && scope.branchId && period.from && period.to) },
  )

  const cfg = useWidgetConfig<BudgetHealthConfig>({ maxRows: DEFAULT_ROWS })

  const rows = React.useMemo<BudgetRow[] | undefined>(() => {
    if (!query.data) return undefined
    return (query.data.budgetSummaries ?? []).slice(0, cfg.config.maxRows).map((summary) => ({
      id: summary.id ?? summary.budgetId,
      title: summary.title,
      planned: summary.plannedExpenses,
      actual: summary.actualExpenses,
      // `actual / planned` on two floats: two amounts that had each already lost precision, divided to answer
      // "have we spent the budget?". The division is now exact integer arithmetic on minor units (IBA-116).
      utilization: moneyUtilizationPercent(summary.actualExpenses, summary.plannedExpenses, summary.currencyCode),
      currency: summary.currencyCode,
      tone: toneForHealth(summary.healthStatus),
    }))
  }, [query.data, cfg.config.maxRows])

  const create = useWidgetCreateActions(WIDGET_CREATE_KEYS)
  useWidgetMenu({
    configure: cfg.openDrawer,
    refresh: () => void query.refetch(),
    viewDetails: go.canNavigateTo(OverviewRoutes.budgets) ? () => go(OverviewRoutes.budgets) : undefined,
    create,
  })

  return (
    <>
    <WidgetBoundary
      isLoading={query.isLoading}
      isError={query.isError}
      isFetching={query.isFetching}
      data={rows}
      isEmpty={(r) => r.length === 0}
      skeleton={<BudgetHealthSkeleton />}
      errorTitle={t("widgets.budgetHealth.error")}
      onRetry={() => void query.refetch()}
      retryLabel={t("widgets.common.retry")}
      emptyTitle={t("widgets.budgetHealth.empty.title")}
      emptyAction={
        // `go.canNavigateTo` as well as the permission: a parish reader inspecting a branch may hold budgeting
        // edit rights of their own, but a "create budget" here would open the flow against the branch they
        // are only looking at, so the button has no honest destination.
        budgeting.canEdit && go.canNavigateTo(OverviewRoutes.createBudget) ? (
          <Button size="sm" variant="outline" onClick={() => go(OverviewRoutes.createBudget)}>
            {t("widgets.budgetHealth.empty.action")}
          </Button>
        ) : undefined
      }
    >
      {(budgetRows) => (
        <ul className="space-y-3">
          {budgetRows.map((row) => (
            <li key={row.id} className="space-y-1.5">
              <div className="flex items-baseline justify-between gap-3">
                <span className="truncate text-sm font-medium text-foreground">{row.title}</span>
                <span className="shrink-0 text-xs font-medium tabular-nums text-muted-foreground">
                  {formatPercent(row.utilization)}
                </span>
              </div>
              <ProgressBar value={row.utilization} tone={row.tone} aria-label={row.title} />
              <p className="text-xs tabular-nums text-muted-foreground">
                {formatMoneySafe(row.actual, row.currency)} / {formatMoneySafe(row.planned, row.currency)}
              </p>
            </li>
          ))}
        </ul>
      )}
    </WidgetBoundary>

    <WidgetConfigDrawer
      open={cfg.open}
      title={t("widgets.budgetHealth.title")}
      onSave={cfg.save}
      onCancel={cfg.cancel}
    >
      <ConfigStepperRow
        label={t("widgets.budgetHealth.config.maxRows")}
        value={cfg.draft.maxRows}
        onChange={(value) => cfg.patch({ maxRows: value })}
        min={2}
        max={6}
      />
    </WidgetConfigDrawer>
    </>
  )
}

function BudgetHealthSkeleton() {
  return (
    <ul className="space-y-3">
      {[0, 1, 2].map((index) => (
        <li key={index} className="space-y-1.5">
          <div className="flex justify-between">
            <Skeleton className="h-3.5 w-28" />
            <Skeleton className="h-3.5 w-8" />
          </div>
          <Skeleton className="h-1.5 w-full rounded-full" />
          <Skeleton className="h-3 w-24" />
        </li>
      ))}
    </ul>
  )
}
